import { Spacer, Text } from '@loomhq/lens';
import classnames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';

import { toggleSidebar } from 'actions/header';
import { showSearchModal } from 'actions/searchModal';
import { HEADER_NAV_LINK_CLICKED } from 'constants/events';
import { GOOGLE, SLACK_APP } from 'constants/oAuthKeys';
import { ROOT_PAGE } from 'constants/routes';
import { AUTHENTICATED_USER_ACCESS, SEARCH_ACCESS } from 'constants/scopes';
import { ADMIN } from 'constants/userRoles';
import { logoutUserCreator } from 'creators/user';
import { hasScope } from 'selectors/scopes';
import * as analytics from 'utilities/analytics';
import * as auth from 'utilities/auth';
import { getAvatarThumbForUser } from 'utilities/avatar';
import * as extension from 'utilities/extension';
import { getParam, isFromPublicSharePage } from 'utilities/url';

import useDowntimeBanner from 'hooks/useDowntimeBanner';

import getItems from './items';
import DropMenu from './menu';
import handleItems from './utilities';
import isUnsafeRedirectUrl from '../../utilities/isUnsafeRedirectUrl';
import { Logo } from './component-types';

import DownloadProgress from 'components/download-progress';
import HelpBubble from 'components/help-bubble';
import Modal from 'components/modal';
import NavigationSidebar from 'components/navigation-sidebar';
import Scopes from 'components/scopes';
import SearchModal from 'components/search-modal';
import ShareButtons from 'components/share-video/share-buttons';
import UserAvatar from 'components/user-avatar';

import './styles.less';

const DowntimeBanner = () => {
  useDowntimeBanner();

  return null;
};

class NavigationBar extends React.Component {
  state = { menu: null };

  toggleSidebar = () => {
    if (this.props.notifDrawerOpen) {
      this.props.toggleNotificationDrawer();
    }

    this.props.toggleSidebar();
  };

  connect = () => {
    auth.openOAuthPopup(SLACK_APP, {}, data => {
      const cameFrom = getParam('redirect_after');
      const errorSuffix = data.error ? `?oauth_error=${data.error}` : '';
      const redirectURL = decodeURIComponent(cameFrom) + errorSuffix;

      if (isUnsafeRedirectUrl(redirectURL)) {
        window.location.href = '/' + errorSuffix;
      }

      if (!data.loginSuccess) {
        window.location.href = redirectURL;

        return;
      }

      extension.signalExtensionLogin(() => {
        window.location.href = redirectURL;
      });
    });
  };

  handleLogoClick = () => {
    analytics.track(HEADER_NAV_LINK_CLICKED, {
      name: 'Logo',
    });
  };

  onMenuToggle = menu => () => {
    if (this.state.menu === menu) {
      menu = null;
    }

    this.setState({ menu });
  };

  onCloseMenu = () => {
    this.setState({ menu: null });
  };

  render() {
    const {
      companyFeatureData,
      currentUserIsOwner,
      showWelcome,
      hasAuthenticatedAccess,
      user,
      video,
      videoIsTeamShared,
      onboardingCompleted,
    } = this.props;
    const variant = this.props.variant || 'app';
    const props = {
      ...this.props,
      variant,
      onMenuToggle: this.onMenuToggle,
      menu: this.state.menu,
      onSlackApp: this.connect,
    };

    return (
      <header
        className={classnames('navigationBar', this.props.variant, {
          loggedout: !hasAuthenticatedAccess,
        })}
      >
        <DowntimeBanner />
        <div className="navigationBar__wrapper">
          <DownloadProgress />
          <Logo
            href={ROOT_PAGE}
            onClick={this.handleLogoClick}
            companyFeatureData={companyFeatureData}
            currentUserIsOwner={currentUserIsOwner}
          />

          <ul className="navigationBar__nav">
            {variant !== 'empty' && (
              <li
                className="navigationBar__navItem navigationBar__hamburger"
                onClick={this.props.toggleSidebar}
              >
                &nbsp;
              </li>
            )}
            {hasAuthenticatedAccess && !onboardingCompleted && user && video ? (
              <li className="flex">
                <ShareButtons
                  currentUserIsOwner={currentUserIsOwner}
                  user={user}
                  video={video}
                  videoIsTeamShared={videoIsTeamShared}
                />
                <Spacer right={2} />
              </li>
            ) : (
              handleItems(getItems(variant), props)
            )}
            {hasAuthenticatedAccess && (showWelcome || !onboardingCompleted) && (
              <li className="navigationBar__navItem">
                <div className="navigationBar__navItem__welcomeAvatar">
                  {this.props.firstName && (
                    <Text>Welcome, {this.props.firstName}!</Text>
                  )}
                  <Spacer left={2} />
                  <UserAvatar />
                </div>
              </li>
            )}
          </ul>

          <DropMenu
            items={getItems('account')}
            onClose={this.onCloseMenu}
            stateProps={props}
            open={this.state.menu === 'account'}
          />
        </div>

        <NavigationSidebar>
          {handleItems(getItems('mobile'), props, null)}
        </NavigationSidebar>
        {hasAuthenticatedAccess && onboardingCompleted && <HelpBubble />}
        <Scopes names={SEARCH_ACCESS}>
          <Modal>
            <SearchModal />
          </Modal>
        </Scopes>
      </header>
    );
  }
}

const mapStateToProps = state => {
  const {
    avatar: { avatars = [] },
    user: {
      current,
      is_logged_in: isLoggedIn,
      is_logged_in_checked: isLoggedInChecked,
    },
    share_video: {
      current_user_is_owner: currentUserIsOwner,
      video,
      videoIsTeamShared,
    },
    welcome: { complete: onboardingCompleted },
    billing: {
      current_plan: currentBillingPlan,
      isTrialing: isTrialUser,
      isFreeUser,
      first_fetch_done: billingFetched,
    } = {},
  } = state;

  const { role, first_name, oauth = {} } = current;

  return {
    avatar: getAvatarThumbForUser(avatars),
    currentUserIsOwner,
    isLoggedInChecked,
    isAdmin: role === ADMIN,
    showGoogleInvites:
      oauth && oauth[GOOGLE] && isFromPublicSharePage().fromPublicSharePage,
    billingFetched,
    isTrialUser,
    isProUser: currentBillingPlan && !isTrialUser,
    isFreeUser,
    firstName: first_name,
    hasAuthenticatedAccess:
      isLoggedIn && hasScope(state, AUTHENTICATED_USER_ACCESS),
    hasSearchAccessScope: hasScope(state, SEARCH_ACCESS),
    user: current,
    video,
    videoIsTeamShared,
    onboardingCompleted,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    logoutUser: () => dispatch(logoutUserCreator()),
    toggleSidebar: () => dispatch(toggleSidebar()),
    showSearchModal: isShortcut => dispatch(showSearchModal(isShortcut)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(NavigationBar);
